import { StrictMode, useEffect } from 'react';

import { excludeGraphQLFetch } from 'apollo-link-sentry';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { RecoilRoot } from 'recoil';

import { FullStory, init as FullStoryInit } from '@fullstory/browser';
import { InkSnackbarProvider } from '@movable/ui';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { fullStoryIntegration } from '@sentry/fullstory';
import * as Sentry from '@sentry/react';
import {
  browserTracingIntegration,
  graphqlClientIntegration,
  reactRouterV6BrowserTracingIntegration,
} from '@sentry/react';

import { getEnv } from '@/components/shared/PrimaryNavDrawer/utils';
import AuthProvider from '@/providers/AuthProvider';

import App from './App';
import ScrollToTop from './components/shared/ScrollToTop';
import InitAppWithApollo from './providers/InitAppWithApollo';
import theme from './theme';

const releaseTag = import.meta.env.VITE_PROJECT_RELEASE_TAG;
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryOrg = import.meta.env.VITE_SENTRY_ORG;

const materialTheme = createTheme(theme);

if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_REACT_SCAN === 'true') {
  import('react-scan').then(({ scan }) => {
    scan({ enabled: true });
  });
}

// Recording local/dev sessions isn't useful and burns session quota.
FullStoryInit({ orgId: '1WFKW', devMode: getEnv() === 'local' });
const removeGraphQLDocument = (event: Sentry.Event) => {
  event.spans?.forEach((span) => {
    delete span.data?.['graphql.document'];
  });

  return event;
};

Sentry.init({
  dsn: sentryDsn,
  release: releaseTag ?? undefined,
  environment: import.meta.env.VITE_ENVIRONMENT,

  integrations: [
    browserTracingIntegration(),
    reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    fullStoryIntegration(sentryOrg, { client: FullStory }),
    graphqlClientIntegration({
      endpoints: [/\/spa-graphql\//],
    }),
  ],

  tracesSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,

  beforeBreadcrumb: (breadcrumb, hint) => {
    const filtered = excludeGraphQLFetch(breadcrumb, hint);

    if (!filtered) return null;

    delete filtered.data?.['graphql.document'];

    return filtered;
  },

  // Scrub GraphQL documents from error events, including
  // errors captured by React Error Boundaries.
  beforeSend: (event) => {
    removeGraphQLDocument(event);
    return event;
  },

  // Scrub GraphQL documents from performance transactions.
  beforeSendTransaction: (event) => {
    removeGraphQLDocument(event);
    return event;
  },
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <RecoilRoot override={false}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <InitAppWithApollo>
              <ThemeProvider theme={materialTheme}>
                <ScrollToTop />
                <CssBaseline />
                <InkSnackbarProvider maxSnack={1}>
                  <App />
                </InkSnackbarProvider>
              </ThemeProvider>
            </InitAppWithApollo>
          </LocalizationProvider>
        </RecoilRoot>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
